import React, { useEffect, useState } from "react";
import { WizardData } from "../index";
import { enrichVinViaServer } from "../../../services/vinApi";
import VinColorField from "../../../components/VinColorField";
import {
  EXTERIOR_COLORS,
  INTERIOR_COLORS,
  resolveColorFromProvider,
} from "../../../constants/vehicleColors";

interface VehicleDetailsStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
  darkMode: boolean;
}

const TITLE_STATUSES = ["Clean", "Salvage", "Rebuilt", "Lemon", "Flood"];

const VehicleDetailsStep: React.FC<VehicleDetailsStepProps> = ({
  data,
  updateData,
  onNext,
  darkMode,
  onBack,
}) => {
  const [enriching, setEnriching] = useState(false);

  // Optional NeoVIN enrichment: colors / version. Failures are silent — identity
  // already came from the cheap Basic decode, and colors stay seller-editable.
  useEffect(() => {
    if (!data.vin || data.enrichmentAttempted) return;
    let cancelled = false;
    setEnriching(true);
    (async () => {
      try {
        const result = await enrichVinViaServer(data.vin);
        if (cancelled) return;
        if (result.ok) {
          const updates: Partial<WizardData> = {
            enrichmentAttempted: true,
            identityVersion: result.identity.version,
            identityDisplayTitle: result.displayTitle,
            identityProviderTier: result.identity.provider?.tier || data.identityProviderTier,
            identityConfidenceRaw: result.identity.confidenceRaw,
            identityConfidenceScore: result.identity.confidenceScore,
            identityStyleKey: result.identity.styleKey,
            identitySnapshot: result.identity,
          };
          const exteriorFromVin = result.identity.enrichment?.exteriorColor;
          const interiorFromVin = result.identity.enrichment?.interiorColor;
          if (exteriorFromVin) {
            updates.suggestedExteriorColor = exteriorFromVin.trim();
            if (!data.exteriorColor) {
              updates.exteriorColor = resolveColorFromProvider(
                EXTERIOR_COLORS,
                exteriorFromVin
              );
            }
          }
          if (interiorFromVin) {
            updates.suggestedInteriorColor = interiorFromVin.trim();
            if (!data.interiorColor) {
              updates.interiorColor = resolveColorFromProvider(
                INTERIOR_COLORS,
                interiorFromVin
              );
            }
          }
          // Prefer a more specific version-backed title when trim was generic.
          if (result.identity.version && (!data.trim || /^base$/i.test(data.trim))) {
            updates.trim = result.identity.trim || data.trim;
          }
          updateData(updates);
        } else {
          updateData({ enrichmentAttempted: true });
        }
      } catch {
        if (!cancelled) updateData({ enrichmentAttempted: true });
      } finally {
        if (!cancelled) setEnriching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.vin, data.enrichmentAttempted]);

  const handleNext = () => {
    if (!data.mileage || parseInt(data.mileage, 10) < 0) {
      alert("Please enter a valid mileage");
      return;
    }
    if (!data.exteriorColor) {
      alert("Please select an exterior color");
      return;
    }
    if (!data.interiorColor) {
      alert("Please select an interior color");
      return;
    }
    onNext();
  };

  return (
    <div className="step-container">
      <h2 className="step-title">Verify Vehicle Details</h2>
      <p className="step-subtitle">
        Confirm the information below and add additional details
      </p>

      <div className="review-section">
        <div className="review-section-title">
          From VIN
          <span
            style={{
              fontSize: "12px",
              fontWeight: "normal",
              color: darkMode ? "#888" : "#666",
            }}
          >
            (Locked)
          </span>
        </div>
        <div className="details-grid">
          <div className="wizard-form-group">
            <div className="detail-label">Year</div>
            <div className="detail-readonly">{data.year || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Make</div>
            <div className="detail-readonly">{data.make || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Model</div>
            <div className="detail-readonly">{data.model || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Trim</div>
            <div className="detail-readonly">
              {data.identityDisplayTitle
                ? data.trim || data.identityVersion || "—"
                : data.trim || "—"}
            </div>
            <div
              style={{
                display: "inline-block",
                marginTop: "6px",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 600,
                background: darkMode ? "#1b3a26" : "#e6f4ea",
                color: darkMode ? "#7fd99a" : "#1e7a34",
              }}
            >
              Auto-filled from VIN
            </div>
            {data.identityVersion && data.identityVersion !== data.trim && (
              <div
                style={{
                  fontSize: "11px",
                  color: darkMode ? "#888" : "#666",
                  marginTop: "4px",
                }}
              >
                Configuration: {data.identityVersion}
              </div>
            )}
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Body Style</div>
            <div className="detail-readonly">{data.bodyStyle || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Drive Type</div>
            <div className="detail-readonly">{data.driveType || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Fuel Type</div>
            <div className="detail-readonly">{data.fuelType || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Transmission</div>
            <div className="detail-readonly">{data.transmission || "—"}</div>
          </div>
          <div className="wizard-form-group">
            <div className="detail-label">Engine</div>
            <div className="detail-readonly">{data.engine || "—"}</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "24px" }}>
        <div className="review-section-title">
          Additional Details
          <span
            style={{
              fontSize: "12px",
              fontWeight: "normal",
              color: darkMode ? "#888" : "#666",
            }}
          >
            (Required{enriching ? " · looking up colors…" : ""})
          </span>
        </div>

        <div className="details-grid">
          <div className="wizard-form-group">
            <label htmlFor="mileage">Mileage *</label>
            <input
              type="number"
              id="mileage"
              className="wizard-input"
              value={data.mileage}
              onChange={(e) => updateData({ mileage: e.target.value })}
              placeholder="e.g. 45000"
              min="0"
              required
            />
          </div>

          <div className="wizard-form-group">
            <label htmlFor="titleStatus">Title Status *</label>
            <select
              id="titleStatus"
              className="wizard-select"
              value={data.titleStatus}
              onChange={(e) => updateData({ titleStatus: e.target.value })}
              required
            >
              {TITLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <VinColorField
            id="exteriorColor"
            label="Exterior Color"
            value={data.exteriorColor}
            suggestedColor={data.suggestedExteriorColor}
            options={EXTERIOR_COLORS}
            onChange={(exteriorColor) => updateData({ exteriorColor })}
            darkMode={darkMode}
          />

          <VinColorField
            id="interiorColor"
            label="Interior Color"
            value={data.interiorColor}
            suggestedColor={data.suggestedInteriorColor}
            options={INTERIOR_COLORS}
            onChange={(interiorColor) => updateData({ interiorColor })}
            darkMode={darkMode}
          />
        </div>
      </div>

      <div className="wizard-buttons">
        <button className="wizard-btn wizard-btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="wizard-btn wizard-btn-primary" onClick={handleNext}>
          Continue to Photos
        </button>
      </div>
    </div>
  );
};

export default VehicleDetailsStep;
